/**
 * Generic `<ProviderBlock>` — a provider-grouped integration row.
 *
 * Renders the outer chrome shared by every CI/CD provider integration:
 * provider mark (SVG/image tile), provider name, subtitle, status badge,
 * an overflow ("kebab") menu for per-installation actions, and a body
 * slot for provider-specific content (repo list, webhook URL, etc.).
 *
 * Lives in `components/` (not workspace-settings-specific) because it is
 * a generic UI primitive: future GitLab / Azure DevOps blocks will consume
 * the same shell. Provider-specific compositions (`GitHubIntegrationBlock`,
 * `WebhookIntegrationBlock`) live next to the surface that uses them.
 *
 * The kebab menu is implemented with a click-outside-close pattern that
 * matches the `NotificationsBell` and `OrgWorkspacePicker` popovers in
 * this codebase — no Radix Popover dependency required for a simple
 * icon-triggered list.
 */
import { useState, type ReactNode } from 'react'
import { KebabMenu, type KebabMenuItem } from '@/components/ui'

// ── Types ────────────────────────────────────────────────────────────────

/** @deprecated Use `KebabMenuItem` from `components/ui`. Kept as an alias
 * so the provider call sites do not churn for a rename that buys them
 * nothing; the shape is identical. */
export type ProviderBlockMenuItem = KebabMenuItem


export interface ProviderBlockProps {
  /** The provider mark — typically a small coloured tile wrapping an SVG. */
  mark: ReactNode
  /** Provider display name, e.g. "GitHub". */
  name: string
  /** One-line subtitle shown below the name (owner, URL, installation ID…). */
  subtitle: ReactNode
  /** Status badge rendered to the right of the meta block. */
  statusBadge: ReactNode
  /** Kebab menu items. If empty the kebab button is hidden. */
  menuItems: ProviderBlockMenuItem[]
  /** Body content (repo list, webhook fields, empty states…). */
  children?: ReactNode
  /** When true, renders the block with a destructive border / background
   * to signal an unhealthy / disconnected state. */
  broken?: boolean
}

// ── Component ─────────────────────────────────────────────────────────────

export function ProviderBlock({
  mark,
  name,
  subtitle,
  statusBadge,
  menuItems,
  children,
  broken = false,
}: ProviderBlockProps) {


  return (
    <div
      className={`rounded-lg border bg-background overflow-visible mb-3 ${
        broken
          ? 'border-destructive/50'
          : 'border-border'
      }`}
    >
      {/* Provider header row */}
      <div
        className={`grid items-center gap-3 px-4 py-3 border-b rounded-t-lg ${
          broken
            ? 'bg-destructive/5 border-destructive/30'
            : 'bg-muted/30 border-border'
        }`}
        style={{ gridTemplateColumns: '38px 1fr auto auto' }}
      >
        {/* Provider mark tile */}
        <div
          className={`w-[38px] h-[38px] rounded-lg flex items-center justify-center flex-shrink-0 ${
            broken ? 'opacity-60' : ''
          }`}
        >
          {mark}
        </div>

        {/* Provider name + subtitle */}
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-tight">{name}</div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</div>
        </div>

        {/* Status badge */}
        {statusBadge}

        {/* The kebab moved to `components/ui/kebabMenu` (#2194), where the
            test list, the open test and each suite now use it too. */}
        <KebabMenu items={menuItems} ariaLabel={`More actions for ${name}`} orientation="horizontal" />
      </div>

      {/* Provider body — slot for repo list, webhook info, etc. */}
      {children && (
        <div className="px-4 py-3">{children}</div>
      )}
    </div>
  )
}

// ── Status badge helpers (used by provider-specific block compositions) ──

/** Green "● Connected" badge. */
export function ConnectedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-300 dark:bg-green-950 dark:text-green-300 dark:border-green-900 whitespace-nowrap">
      ● Connected
    </span>
  )
}

/** Red "● Disconnected" badge. */
export function DisconnectedBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-900 whitespace-nowrap">
      ● Disconnected
    </span>
  )
}

// ── Owner avatar helper (used by GitHubIntegrationBlock) ──────────────────

/** Hashes a string (owner login) to a palette index so the initials
 * chip colour is deterministic and stable across renders / refreshes. */
function hashStringToIndex(s: string, len: number): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h % len
}

const AVATAR_PALETTE = [
  'from-indigo-500 to-indigo-900',
  'from-sky-500 to-sky-900',
  'from-orange-500 to-orange-900',
  'from-emerald-500 to-emerald-900',
  'from-violet-500 to-violet-900',
  'from-rose-500 to-rose-900',
  'from-amber-500 to-amber-900',
  'from-teal-500 to-teal-900',
]

/** 28-pixel circular avatar with a GitHub avatar image and a deterministic
 * letter-initials fallback when the image is unavailable or missing.
 *
 * The fallback is implemented as an `<img>` `onError` handler that hides
 * the image and reveals the initials chip — same pattern as the member-
 * list avatar in `OrgWorkspacePicker`. */
export function OwnerAvatar({
  login,
  avatarUrl,
  size = 28,
}: {
  login: string
  avatarUrl?: string
  size?: number
}) {
  const [imgFailed, setImgFailed] = useState(false)
  const initials = (login.slice(0, 2)).toUpperCase()
  const palette = AVATAR_PALETTE[hashStringToIndex(login, AVATAR_PALETTE.length)]!
  const dim = `${size}px`

  if (avatarUrl && !imgFailed) {
    return (
      <img
        src={avatarUrl}
        alt={login}
        width={size}
        height={size}
        className="rounded-full flex-shrink-0"
        style={{ width: dim, height: dim }}
        onError={() => setImgFailed(true)}
      />
    )
  }

  return (
    <div
      role="img"
      aria-label={login}
      className={`rounded-full bg-gradient-to-br ${palette} flex items-center justify-center text-white font-bold flex-shrink-0`}
      style={{ width: dim, height: dim, fontSize: Math.floor(size * 0.36) }}
    >
      <span aria-hidden="true">{initials}</span>
    </div>
  )
}
